import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Button from 'components/button';
import EmptyState from 'components/EmptyState/EmptyState';
import useOpenInvoiceDetail from 'components/invoiceDetail/useOpenInvoiceDetail';
import useModal from 'components/modal/useModal';
import Pagination from 'components/pagination/pagination';
import ScrollableTableWrapper from 'components/table/components/ScrollableTableWrapper';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import {
  INVOICE_LITIGATION_MODAL,
  INVOICE_REMINDER_MODAL,
  REMINDER_TABS,
  countText,
  formatFullDay,
  formatReminderEuros,
  invoiceRemindersText,
} from './invoiceRemindersText';
import ReminderRow from './ReminderRow';
import RemindersSummaryCards from './RemindersSummaryCards';
import useInvoiceReminderChanged from './useInvoiceReminderChanged';
import useInvoiceReminderList, {
  REMINDERS_PAGE_SIZE,
} from './useInvoiceReminderList';

export { INVOICE_REMINDER_MODAL };

const SKELETON_ROWS = 5;
const HEADER_CELL_CLASS_NAME =
  'sticky top-0 z-30 py-3.5 text-left text-sm font-medium text-gray-700 bg-gray-50 border-b border-gray-100 whitespace-nowrap';

// The total under each tab: what "À faire" owes is late; "À venir" is not all late yet.
const TOTAL_TEXTS = {
  todo: 'list.totalLate',
  litigation: 'list.totalLitigation',
  upcoming: 'list.totalOwed',
};

// The search key of the unfiltered list, the only one the summary counts.
const UNSEARCHED_KEY = JSON.stringify(['', '']);
// The tabs whose total the summary gives again once it is read again.
const SUMMARY_TABS = ['todo', 'litigation'];

// The "Contentieux" tab says since when (mockup 7.2, plus the "Passage" of the plan § 0).
const columnsOf = (tab) =>
  tab === 'litigation'
    ? ['number', 'company', 'remaining', 'late', 'passage', 'status']
    : ['number', 'company', 'remaining', 'late', 'step'];

const TableHead = ({ tab, text }) => (
  <thead className="sticky top-0 z-10">
    <tr>
      {columnsOf(tab).map((column, index) => (
        <th
          key={column}
          className={`${HEADER_CELL_CLASS_NAME} ${index === 0 ? 'px-4' : 'px-2'}`}
        >
          {text(`columns.${column}`).toUpperCase()}
        </th>
      ))}
      <th className={`${HEADER_CELL_CLASS_NAME} pl-2 pr-3`} />
    </tr>
  </thead>
);

const LitigationBanner = ({ total, text }) => (
  <div
    role="note"
    data-testid="litigation-banner"
    className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800"
  >
    <div className="font-medium">
      {countText(text, 'litigation.bannerTitle', total)}
    </div>
    <div>{text('litigation.bannerText')}</div>
  </div>
);

const SubTabs = ({ tab, onChange, totals, text }) => (
  <div
    role="tablist"
    aria-label={text('tabs.label')}
    className="flex flex-wrap items-center gap-2"
  >
    {REMINDER_TABS.map((name) => {
      const isActive = name === tab;
      const total = totals[name];
      return (
        <button
          key={name}
          type="button"
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(name)}
          className={`flex items-center gap-2 h-9 px-3 rounded-md text-sm ${
            isActive
              ? 'bg-gray-100 text-gray-900'
              : 'text-gray-500 hover:bg-gray-100'
          }`}
        >
          <span>{text(`tabs.${name}`)}</span>
          {Number.isInteger(total) && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded bg-yellow-100 text-xs font-medium text-yellow-800">
              {total}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

/**
 * The reminders of unpaid invoices on "Paie & factures" (JBWEB-1283, mockup
 * 7.1): the cards of the summary, the sub-tabs, one page of the tab with its
 * loading, empty and failed states, the total and the pages.
 *
 * `filters` (`q`, `scope`) come from the header's search; `refreshKey`
 * changes when a "Tout relancer" run ends. `summary` and `onReloadSummary`
 * come from the page, which shows the counter of the tab.
 */
const InvoiceRemindersBoard = ({
  summary,
  onReloadSummary,
  filters,
  onClearFilters,
  refreshKey,
}) => {
  const { language, setModalData } = UseContext();
  const text = invoiceRemindersText(language);
  const { toggle } = useModal();
  const openInvoiceDetail = useOpenInvoiceDetail();
  const [tab, setTab] = useState('todo');
  const reminders = useInvoiceReminderList({
    tab,
    q: filters?.q,
    scope: filters?.scope,
    refreshKey,
  });
  const { list, page, setPage, updateItem, reload } = reminders;

  // The last total read for each tab and search: shown under the tabs not on screen.
  const [knownTotals, setKnownTotals] = useState({});
  const searchKey = JSON.stringify([
    filters?.q ?? '',
    filters?.q ? filters?.scope : '',
  ]);
  useEffect(() => {
    if (!list) return;
    setKnownTotals((current) => ({
      ...current,
      [`${tab}|${searchKey}`]: list.total,
    }));
  }, [list, tab, searchKey]);

  const totals = Object.fromEntries(
    REMINDER_TABS.map((name) => {
      if (name === tab && list) return [name, list.total];
      const known = knownTotals[`${name}|${searchKey}`];
      if (Number.isInteger(known)) return [name, known];
      // The summary counts without a search.
      if (!filters?.q && summary) {
        if (name === 'todo') return [name, summary.toDo];
        if (name === 'litigation') return [name, summary.litigation?.count];
      }
      return [name, undefined];
    }),
  );

  // One change at a time per invoice: a double click must not send two.
  const pendingSuspensions = useRef(new Set());
  const [, setPendingCount] = useState(0);

  const toggleSuspension = useCallback(
    (item) => {
      const { number } = item;
      if (pendingSuspensions.current.has(number)) return;
      pendingSuspensions.current.add(number);
      setPendingCount(pendingSuspensions.current.size);
      const done = () => {
        pendingSuspensions.current.delete(number);
        setPendingCount(pendingSuspensions.current.size);
      };
      ApiDatabase.putInvoiceReminderSuspension(
        { number, suspended: !item.suspended },
        (data) => {
          done();
          const suspended = Boolean(data?.suspended);
          updateItem(number, { suspended });
          if (suspended) {
            toast.success(text('suspension.suspended', { number }));
            return;
          }
          const nextDay = formatFullDay(data?.nextAutomaticAt);
          toast.success(
            nextDay
              ? text('suspension.resumedNext', { number, date: nextDay })
              : text('suspension.resumedNone', { number }),
          );
        },
        (error) => {
          if (error?.name === 'CanceledError') return;
          done();
          // Never the error object in the console: axios keeps the request, token included.
          const code = error?.response?.data?.errorCode;
          if (
            code === 'INVOICE_NOT_REMINDABLE' ||
            code === 'INVOICE_NOT_FOUND'
          ) {
            toast.error(text(`suspension.errors.${code}`));
            reload();
            onReloadSummary?.();
            return;
          }
          toast.error(text('suspension.errors.generic'));
        },
      );
    },
    [text, updateItem, reload, onReloadSummary],
  );

  const openReminder = (item) => {
    setModalData({ number: item.number });
    toggle(INVOICE_REMINDER_MODAL);
  };

  const openLitigation = (item, action) => {
    setModalData({ number: item.number, action });
    toggle(INVOICE_LITIGATION_MODAL);
  };

  const retry = () => {
    reload();
    onReloadSummary?.();
  };

  // A reminder sent by hand (or refused because the invoice moved), or a move
  // to or out of litigation, changes the tabs: the page is read again. The
  // summary reads itself again on the same return (useRemindersSummary), so
  // it is not asked twice here. Only the unsearched totals it covers are
  // forgotten for its own: "À venir" and "Réglées", and any searched total,
  // have nothing to replace them and keep their last value.
  useInvoiceReminderChanged(() => {
    reload();
    setKnownTotals((current) => {
      const next = { ...current };
      SUMMARY_TABS.forEach((name) => {
        delete next[`${name}|${UNSEARCHED_KEY}`];
      });
      return next;
    });
  });

  let content;
  if (reminders.failed) {
    // Never the empty state: a list that could not be read is not an empty one.
    content = (
      <div
        role="alert"
        className="flex flex-col items-center gap-3 py-16 text-center text-gray-700"
      >
        <div>{text('list.loadFailed')}</div>
        <Button color="SECONDARY" onClick={retry}>
          {text('list.retry')}
        </Button>
      </div>
    );
  } else if (!list) {
    content = (
      <div className="flex flex-col flex-1 min-h-0">
        <ScrollableTableWrapper>
          <table
            className="table-auto w-full text-gray-500 ft-sm"
            aria-busy="true"
          >
            <TableHead tab={tab} text={text} />
            <tbody>
              {Array.from({ length: SKELETON_ROWS }, (_, row) => (
                <tr key={row}>
                  {/* One more for the actions. */}
                  {Array.from(
                    { length: columnsOf(tab).length + 1 },
                    (__, column) => (
                      <td key={column} className="px-2 py-7">
                        <div className="h-2 bg-gray-200 rounded animate-pulse" />
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollableTableWrapper>
        <div
          role="status"
          className="border-t border-gray-100 bg-white px-8 py-4 text-sm text-gray-500"
        >
          {text('list.loading')}
        </div>
      </div>
    );
  } else if (list.total === 0 && reminders.isFiltered) {
    content = (
      <EmptyState
        type="invoice"
        title={text('list.filteredEmptyTitle')}
        // Never `undefined`: EmptyState would fall back to its own description.
        description=""
        action={{ label: text('list.clearSearch'), onClick: onClearFilters }}
      />
    );
  } else if (list.total === 0) {
    content = (
      <EmptyState
        type="invoice"
        title={text(`list.empty.${tab}.title`)}
        description={text(`list.empty.${tab}.text`)}
      />
    );
  } else {
    const countText =
      list.pages > 1
        ? text('list.showingRange', {
            start: REMINDERS_PAGE_SIZE * (page - 1) + 1,
            end: Math.min(REMINDERS_PAGE_SIZE * page, list.total),
            total: list.total,
          })
        : text('list.showingAll', { total: list.total });
    const totalKey = TOTAL_TEXTS[tab];
    content = (
      <div className="flex flex-col flex-1 min-h-0 gap-4">
        {tab === 'litigation' && (
          <LitigationBanner total={list.total} text={text} />
        )}
        <ScrollableTableWrapper>
          <table className="table-auto w-full text-gray-500 ft-sm">
            <TableHead tab={tab} text={text} />
            <tbody>
              {(list.items ?? []).map((item) => (
                <ReminderRow
                  key={item.number}
                  item={item}
                  tab={tab}
                  text={text}
                  onOpen={openInvoiceDetail}
                  onRemind={openReminder}
                  onToggleSuspension={toggleSuspension}
                  isSuspensionPending={pendingSuspensions.current.has(
                    item.number,
                  )}
                  onLitigation={openLitigation}
                />
              ))}
            </tbody>
          </table>
        </ScrollableTableWrapper>
        <div className="flex-shrink-0 flex flex-col gap-3 md:flex-row md:items-center border-t border-gray-100 bg-white px-8 py-4">
          <div className="flex-1">
            <Pagination
              currentPage={page}
              countPerPage={REMINDERS_PAGE_SIZE}
              countTotal={list.total}
              totalPages={list.pages}
              setCurrentPage={setPage}
              customText={countText}
            />
          </div>
          {totalKey && (
            <div className="border rounded-md py-2 px-3 text-sm font-medium bg-gray-50 whitespace-nowrap">
              {text(totalKey, {
                amount: formatReminderEuros(list.totalRemaining),
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 flex-1 min-h-0">
      <RemindersSummaryCards summary={summary} text={text} />
      <SubTabs tab={tab} onChange={setTab} totals={totals} text={text} />
      <div className="flex flex-col flex-1 min-h-0">{content}</div>
    </div>
  );
};

export default InvoiceRemindersBoard;
